// Merchant flyer page — upload (subscription-gated), list, toggle, delete.

import React, { useEffect, useState } from 'react';
import { deleteFlyer, listFlyers, toggleFlyer, uploadFlyer } from '../../services/merchantService';
import DataTable from '../common/DataTable';
import ConfirmDialog from '../common/ConfirmDialog';
import PortalModal, { ModalActions } from '../common/PortalModal';
import Field from '../common/Field';

const today = () => new Date().toISOString().slice(0, 10);
const plus = (days) => new Date(Date.now() + days * 86400000).toISOString().slice(0, 10);

export default function MerchantFlyers() {
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [expiryFilter, setExpiryFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ valid_from: today(), valid_until: plus(7), image: null });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmDel, setConfirmDel] = useState(null);

  const reload = () => listFlyers().then(d => setRows(d.flyers || [])).catch(() => {});
  useEffect(() => { reload(); }, []);

  const todayStr = new Date().toISOString().slice(0, 10);
  const visible = rows.filter(f => {
    if (statusFilter === 'active' && !f.is_active) return false;
    if (statusFilter === 'inactive' && f.is_active) return false;
    if (expiryFilter === 'active' && f.valid_until < todayStr) return false;
    if (expiryFilter === 'expired' && f.valid_until >= todayStr) return false;
    return true;
  });

  const resetFilters = () => { setStatusFilter(''); setExpiryFilter(''); };
  const hasFilters = statusFilter || expiryFilter;

  const upload = async () => {
    if (!form.image) { setError('Image is required.'); return; }
    setBusy(true);
    setError('');
    try {
      await uploadFlyer(form);
      setShowForm(false);
      setForm({ valid_from: today(), valid_until: plus(7), image: null });
      reload();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 402) {
        setError('Subscription required — your subscription must be active to upload flyers.');
      } else {
        setError(e?.response?.data?.error || e.message);
      }
    } finally { setBusy(false); }
  };

  const doToggle = async (f) => { await toggleFlyer(f.id); reload(); };
  const doDelete = async () => { if (!confirmDel) return; await deleteFlyer(confirmDel.id); setConfirmDel(null); reload(); };

  return (
    <>
      <div className="portal-content-header">
        <h1>My flyers</h1>
        <button type="button" className="portal-button" onClick={() => { setShowForm(true); setError(''); }}>+ Upload flyer</button>
      </div>

      <div className="portal-filter-bar">
        <select className="portal-search" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All status</option>
          <option value="active">Live</option>
          <option value="inactive">Paused</option>
        </select>
        <select className="portal-search" value={expiryFilter} onChange={e => setExpiryFilter(e.target.value)}>
          <option value="">All validity</option>
          <option value="active">Still valid</option>
          <option value="expired">Expired</option>
        </select>
        {hasFilters && <button type="button" className="portal-button secondary" onClick={resetFilters}>Clear</button>}
        <span className="meta">{visible.length} of {rows.length} flyers</span>
      </div>

      <DataTable
        columns={[
          { key: 'store_name', label: 'Store', render: f => <strong>{f.store_name}</strong> },
          { key: 'deals_count', label: 'Deals' },
          { key: 'valid_from', label: 'From' },
          { key: 'valid_until', label: 'Until' },
          { key: 'is_active', label: 'Status', render: f => <span className={`badge badge-${f.is_active ? 'active' : 'inactive'}`}>{f.is_active ? 'Live' : 'Off'}</span> },
          { key: 'parsed_at', label: 'Parsed', render: f => f.parsed_at ? new Date(f.parsed_at).toLocaleString() : '—' },
          { key: 'actions', label: '', render: f => (
            <div className="row-actions">
              <button type="button" className="portal-button secondary" onClick={() => doToggle(f)}>{f.is_active ? 'Pause' : 'Activate'}</button>
              <button type="button" className="portal-button danger" onClick={() => setConfirmDel(f)}>Delete</button>
            </div>
          ) },
        ]}
        rows={visible}
        emptyMessage={hasFilters ? 'No flyers match the current filters.' : 'No flyers uploaded yet.'}
      />

      <PortalModal
        open={showForm}
        onClose={() => setShowForm(false)}
        title="Upload flyer"
        size="narrow"
      >
        <p>
          GPT-4 Vision parses the image into individual deal rows. The image itself
          is not retained on our servers.
        </p>
        {error && <div className="portal-error">{error}</div>}
        <div className="portal-form-grid">
          <Field label="Valid from">
            <input type="date" className="portal-search" value={form.valid_from} onChange={e => setForm({ ...form, valid_from: e.target.value })} />
          </Field>
          <Field label="Valid until">
            <input type="date" className="portal-search" value={form.valid_until} onChange={e => setForm({ ...form, valid_until: e.target.value })} />
          </Field>
          <Field label="Flyer photo (JPG / PNG)" full>
            <input type="file" accept="image/*" onChange={e => setForm({ ...form, image: e.target.files?.[0] || null })} />
          </Field>
        </div>
        <ModalActions>
          <button type="button" className="portal-button secondary" onClick={() => setShowForm(false)}>Cancel</button>
          <button type="button" className="portal-button" disabled={busy} onClick={upload}>{busy ? 'Parsing…' : 'Upload & parse'}</button>
        </ModalActions>
      </PortalModal>

      <ConfirmDialog
        open={!!confirmDel}
        title="Delete flyer?"
        message={`Delete "${confirmDel?.store_name}" flyer with ${confirmDel?.deals_count || 0} parsed deals? This cannot be undone.`}
        confirmLabel="Delete"
        danger
        onConfirm={doDelete}
        onCancel={() => setConfirmDel(null)}
      />
    </>
  );
}
